import { Component, Fragment, Prop, Watch, h } from '@stencil/core';

import actions from '../../../../actions/actions.counteroffer';
import { CounterofferStep } from '../../../../interfaces/interface.offer';
import track from '../../../../tracking/tracking.counteroffer';
import { formatAmount, isClosedByUser, parseAmount, scheduleAutoClosing } from '../../../../utils/utils.counteroffer';
import { BulletList, Footer, ModalHeader, YourCounteroffer } from '../Shared';

@Component({
  tag: 'counteroffer-dialog',
  styleUrl: 'counteroffer-dialog.css',
})
export class CounterofferModal {
  @Prop() inputAmount: number = null;
  @Prop() currentStep: number = CounterofferStep.Input;
  @Prop() error: string = null;
  @Prop() message: string = null;
  @Prop() isSubmitting: boolean = false;
  @Prop() offerAmount: string = null;

  @Prop({ mutable: true }) modal: HTMLIcoModalElement;

  onAmountChanged = ({ detail: amount }) => {
    actions.onUpdateInputAmount(amount, this.offerAmount);
  };

  onAmountBlur = () => {
    actions.onInputBlur(this.offerAmount);
  };

  makeCounteroffer = () => {
    actions.onMoveToReview();
  };

  editCounteroffer = () => {
    actions.onReturnToInput();
  };

  submitCounteroffer = () => {
    actions.onSubmitCounteroffer();
  };

  onClosing = () => {
    actions.onResetCounterofferState();
    if (isClosedByUser(this.modal)) {
      track.counteroffer.close(this.submitted);
    }
  };

  @Watch('currentStep')
  onStepChanged() {
    if (this.submitted) {
      scheduleAutoClosing(this.modal);
    }
  }

  get submitted() {
    return this.currentStep === CounterofferStep.Submitted;
  }

  render() {
    return (
      <ico-counteroffer-modal displayX class="font-open-sans" dataTestId="counteroffer" shouldCloseOnBackdrop={false} ref={(el) => (this.modal = el)} callback={this.onClosing}>
        {this.renderCurrentStep()}

        <div class="text-label mt-8 sm:mt-4">
          <Footer />
        </div>
      </ico-counteroffer-modal>
    );
  }

  renderCurrentStep() {
    return {
      [CounterofferStep.Input]: this.renderInputStep(),
      [CounterofferStep.Review]: this.renderReviewStep(),
      [CounterofferStep.Submitted]: this.renderSubmittedStep(),
    }[this.currentStep];
  }

  renderInputStep() {
    return (
      <>
        <ModalHeader>
          What's Your Counteroffer?
          <span>Making a fair and reasonable counteroffer gives you the opportunity to negotiate a deal that works better for you.</span>
        </ModalHeader>
        <h2 class="text-base pt-3 mdLrg:pt-2">
          <div>
            Your Kelley Blue Book<span>&#174;</span> Instant Cash Offer 
          </div>
        </h2>
        <div class="text-5xl mb-3 font-extrabold font-montserrat text-clrs-green">
          {this.offerAmount}
        </div>
        <div class="flex max-sm:flex-col sm:flex-row justify-between max-sm:items-start sm:items-end">
          <div class="max-sm:w-full sm:w-60 max-sm:order-1">
            <span class="text-sm text-[#6D6D6D] select-none">Counteroffer Amount</span>
            <input-text-box
              label="What’s the amount?"
              hasGrayPlaceHolder
              testId="counteroffer-amount"
              parse={parseAmount}
              format={formatAmount}
              value={this.inputAmount?.toString() || ''}
              errorMessage={this.error}
              hasError={!!this.error}
              hasAlertIcon
              onValueChange={this.onAmountChanged}
              onBlur={this.onAmountBlur}
            />
            {this.message && (
              <div class="hidden max-sm:flex mt-1 gap-[4px] text-[#2B5195] text-[10px] max-sm:w-full max-sm:order-2">
                <span>
                  <svg-icon width="16px" height="16px" icon="alert" aria-label="Information icon" />
                </span>
                <span>{this.message}</span>
              </div>
            )}
          </div>
          <ico-button
            label="Make a Counteroffer"
            buttonSize="large"
            onClick={this.makeCounteroffer}
            class={{
              'sm:mb-5 mt-1': !!this.error,
              'max-sm:mt-6': !this.error,
              'max-sm:w-full max-sm:order-3': true,
            }}
            data-testid="make-counteroffer-btn"
          />
        </div>
         {this.message && (
            <div class="hidden sm:flex mt-2 gap-[4px] text-[#2B5195] text-[10px]">
              <span>
                <svg-icon width="16px" height="16px" icon="alert" aria-label="Information icon" />
              </span>
              <span>{this.message}</span>
            </div>
          )}
      </>
    );
  }

  renderReviewStep() {
    return (
      <>
        <ModalHeader>Review and Submit Your Counteroffer</ModalHeader>

        <div>
          <YourCounteroffer amount={this.inputAmount}>
            <BulletList>
              This is an opportunity for you to:
              <li>
                Help the dealer better understand your cash or <span class="inline-block">trade-in</span> expectation.
              </li>
              <li>Save time at the dealership.</li>
            </BulletList>
          </YourCounteroffer>

          <div class="flex gap-4 max-sm:flex-col-reverse justify-end mt-4 sm:mt-1">
            <ico-button
              label="Edit Counteroffer"
              buttonSize="large"
              buttonStyle="tertiary"
              onClick={this.editCounteroffer}
              data-testid="edit-counteroffer-btn"
              disabled={this.isSubmitting}
            />
            <ico-button
              label={this.isSubmitting ? 'Submitting...' : 'Submit Counteroffer'}
              buttonSize="large"
              onClick={this.submitCounteroffer}
              data-testid="submit-counteroffer-btn"
              disabled={this.isSubmitting}
            />
          </div>
        </div>
      </>
    );
  }

  renderSubmittedStep() {
    return (
      <>
        <ModalHeader>Your Counteroffer has been successfully submitted!</ModalHeader>

        <YourCounteroffer amount={this.inputAmount}>
          <BulletList>
            What happens next:
            <li>A participating dealer will reach out to you about your Offer.</li>
          </BulletList>
        </YourCounteroffer>
      </>
    );
  }
}
